import { useRef, useState } from "preact/hooks"

import { ShortPayDisputeDialog } from "@/accounting/ShortPayDisputeDialog.tsx"
import { ShortPayInvoiceDialog, ShortPayInvoiceRef } from "@/accounting/ShortPayInvoiceDialog.tsx"
import { Link } from "wouter-preact"
import { AlertTriangle, ArrowLeft, Loader2, Search, X } from "lucide-react"

import {
  DetectedShortPayJson,
  ShortPayJson,
  ShortPayListResponse,
  ShortPayPatternJson,
  ShortPayStatus,
  listShortPays,
  recordShortPay,
  updateShortPay,
} from "@/api/accounting.tsx"
import {
  open_short_pays,
  pattern_matches_row,
  sum_amounts,
  sum_outstanding,
  total_exposure,
} from "@/accounting/short_pay_display.ts"
import {
  DetectedShortPaysTable,
  RecordedShortPaysTable,
  ShortPayPatterns,
  detected_row_key,
} from "@/accounting/short_pay_tables.tsx"
import { UserState } from "@/auth/user"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { toast } from "@/components/ui/use-toast"
import { exactCurrencyFormatter, useAsyncEffect, usePageTitle } from "@/utils"

const STATUS_TABS: { key: ShortPayStatus | "all"; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "ignored", label: "Ignored" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All" },
]

const EMPTY: ShortPayListResponse = {
  eligible: false,
  invoice_pdf_available: false,
  synced_invoice_count: 0,
  recorded: [],
  detected: [],
  patterns: [],
  min_amount: 0,
  months: 0,
}

function ask_note(action: string, current?: string): string | null | undefined {
  const input = window.prompt(`Optional note for marking this short pay ${action}:`, current ?? "")
  if (input === null) {
    return null
  }
  return input.trim() || undefined
}

/**
 * Short pays: invoices the customer settled for less than their accounting-system total. Two
 * populations — the ones flagged when cash was applied through MarginWiz, and the ones found by
 * comparing every synced invoice against what our distributor/retailer data says was paid for it.
 * System-admin only for now, and only for orgs with an accounting integration that has synced
 * invoices (the sidebar entry is gated the same way).
 */
export function ShortPaysPage() {
  usePageTitle("Short Pays")
  const user = UserState.use((u) => u!)
  const [data, setData] = useState<ShortPayListResponse>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<ShortPayStatus | "all">("open")
  const [savingId, setSavingId] = useState<string | null>(null)
  /** The short pay whose dispute dialog is open, if any. */
  const [disputing, setDisputing] = useState<ShortPayJson | null>(null)
  /** The synced invoice whose accounting-system detail (fields + PDF) is open, if any. */
  const [viewing_invoice, setViewingInvoice] = useState<ShortPayInvoiceRef | null>(null)
  /** The pattern warning currently filtering the detected table, if any. */
  const [pattern_filter, setPatternFilter] = useState<ShortPayPatternJson | null>(null)
  const detected_card_ref = useRef<HTMLDivElement>(null)

  async function load() {
    const res = await listShortPays()
    if (res.ok) {
      setData(res.value.data)
    } else {
      toast({ title: "Failed to load short pays", description: res.error.message, variant: "destructive" })
    }
  }

  useAsyncEffect(async () => {
    setLoading(true)
    await load()
    setLoading(false)
  }, [])

  /** After filing, reload so the row shows as disputed rather than still offering the action. */
  async function on_dispute_filed() {
    toast({
      title: "Short pay dispute sent",
      description: "We'll track the reply in the thread. Watch your mailbox for their response.",
    })
    await load()
  }

  async function set_status(row: ShortPayJson, status: ShortPayStatus) {
    let note: string | undefined
    if (status === "resolved" || status === "ignored") {
      const input = ask_note(status, row.resolved_note)
      if (input === null) {
        return
      }
      note = input
    }
    setSavingId(row.id)
    const res = await updateShortPay(row.id, status, note)
    setSavingId(null)
    if (res.ok) {
      setData((prev: ShortPayListResponse) => ({
        ...prev,
        recorded: prev.recorded.map((r: ShortPayJson) =>
          r.id === row.id ? { ...r, status, resolved_note: note ?? r.resolved_note } : r
        ),
      }))
    } else {
      toast({ title: "Failed to update short pay", description: res.error.message, variant: "destructive" })
    }
  }

  /**
   * Record a detected row as a short pay, moving it from the detected list into the tracked one.
   * Returns the recorded row so a caller can act on it (the dispute flow opens its dialog on it).
   */
  async function record(row: DetectedShortPayJson, status: ShortPayStatus, note?: string): Promise<ShortPayJson | null> {
    const key = detected_row_key(row)
    setSavingId(key)
    const res = await recordShortPay(row.accounting_invoice_id, row.deduction_id, status, note)
    setSavingId(null)
    if (!res.ok) {
      toast({ title: "Failed to record short pay", description: res.error.message, variant: "destructive" })
      return null
    }
    const saved = res.value.data
    const recorded_row: ShortPayJson = {
      id: saved.id,
      created_at: saved.created_at,
      amount: row.amount,
      status,
      resolved_note: note,
      accounting_invoice_id: row.accounting_invoice_id,
      deduction_id: row.deduction_id,
      invoice_number: row.invoice_number,
      invoice_total: row.invoice_total,
      check_number: row.check_number,
      check_date: row.check_date,
      customer_name: row.customer_name,
    }
    setData((prev: ShortPayListResponse) => ({
      ...prev,
      detected: prev.detected.filter((d: DetectedShortPayJson) => detected_row_key(d) !== key),
      recorded: [recorded_row, ...prev.recorded],
    }))
    return recorded_row
  }

  /** Track/ignore a detected row, prompting for the optional note like the recorded actions do. */
  async function record_with_note(row: DetectedShortPayJson, status: ShortPayStatus) {
    let note: string | undefined
    if (status === "ignored") {
      const input = ask_note(status)
      if (input === null) {
        return
      }
      note = input
    }
    await record(row, status, note)
  }

  /**
   * Dispute a detected row directly: record it as an open short pay first (the dispute needs a row
   * to hang off), then go straight into the review-and-send dialog.
   */
  async function dispute_detected(row: DetectedShortPayJson) {
    const recorded_row = await record(row, "open")
    if (recorded_row) {
      setDisputing(recorded_row)
    }
  }

  /** Filter the detected table to a pattern's invoices and bring it into view. */
  function select_pattern(pattern: ShortPayPatternJson | null) {
    setPatternFilter(pattern)
    if (pattern) {
      detected_card_ref.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  if (user.role !== "system_admin") {
    return (
      <div class="container mx-auto px-4 py-10 max-w-3xl text-center text-slate-600">
        Short pays are only visible to system admins right now.
      </div>
    )
  }

  const open_rows = open_short_pays(data.recorded)
  const visible_recorded = tab === "all" ? data.recorded : data.recorded.filter((r: ShortPayJson) => r.status === tab)
  const visible_detected = pattern_filter
    ? data.detected.filter((row: DetectedShortPayJson) => pattern_matches_row(pattern_filter, row))
    : data.detected

  return (
    <div class="container mx-auto px-4 py-10 max-w-6xl space-y-6">
      <div class="flex items-center gap-4">
        <Link to="/accounting/checks">
          <Button variant="ghost" size="icon" class="h-10 w-10 shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div class="min-w-0">
          <h1 class="text-2xl font-bold tracking-tight text-slate-900">Short Pays</h1>
          <p class="text-slate-600 mt-0.5 text-sm sm:text-base">
            Invoices settled below their accounting-system total — flagged when cash was applied, and found by comparing
            every synced invoice against what the customer actually paid
          </p>
        </div>
      </div>

      {loading ? (
        <div class="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : !data.eligible ? (
        <Card class="shadow-sm border-slate-200/80">
          <CardHeader class="space-y-1 pb-4">
            <CardTitle class="text-lg font-semibold tracking-tight">Nothing to compare against yet</CardTitle>
            <CardDescription>
              {data.accounting_system
                ? `${data.accounting_system} is connected, but no invoices have synced from it yet. Short pays compare synced
                   invoice totals against what customers actually paid, so this screen turns on after the first invoice sync.`
                : `Short pays compare accounting-system invoice totals against what customers actually paid. Connect an
                   accounting integration (QuickBooks, Xero, NetSuite or Sage Intacct) and sync invoices to use this screen.`}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          <Card class="shadow-sm border-slate-200/80">
            <CardHeader class="space-y-1 pb-4">
              <CardTitle class="flex items-start gap-2.5 text-lg font-semibold tracking-tight leading-snug">
                <AlertTriangle className="size-5 shrink-0 text-amber-600 mt-0.5" />
                <span>
                  {exactCurrencyFormatter(total_exposure(data.recorded, data.detected))} short across{" "}
                  {open_rows.length + data.detected.length}{" "}
                  {open_rows.length + data.detected.length === 1 ? "invoice" : "invoices"}{" "}
                  <span class="font-normal text-slate-500">
                    · {exactCurrencyFormatter(sum_outstanding(data.detected))} still due
                  </span>
                </span>
              </CardTitle>
              <CardDescription>
                Compared {data.synced_invoice_count.toLocaleString()} invoices synced from {data.accounting_system} against
                payments from the last {data.months} months, ignoring gaps under {exactCurrencyFormatter(data.min_amount)}.
                "Still due" is what the accounting system says is left on the invoice — a gap it already credited or wrote
                off shows $0 there, and is worth knowing about rather than chasing.
              </CardDescription>
            </CardHeader>
            {data.patterns.length > 0 && (
              <CardContent class="pt-0">
                <ShortPayPatterns patterns={data.patterns} selected={pattern_filter} on_select={select_pattern} />
              </CardContent>
            )}
          </Card>

          <Card class="shadow-sm border-slate-200/80" ref={detected_card_ref}>
            <CardHeader class="space-y-1 pb-4">
              <CardTitle class="flex items-start gap-2.5 text-lg font-semibold tracking-tight leading-snug">
                <Search className="size-5 shrink-0 text-slate-500 mt-0.5" />
                <span>
                  {visible_detected.length} detected · {exactCurrencyFormatter(sum_amounts(visible_detected))}
                </span>
              </CardTitle>
              <CardDescription>
                Invoices our distributor and retailer data says were paid short, whether or not cash was ever applied here,
                ranked by what is still due. Dispute one to chase it, track it to work it later, or ignore a known
                contractual fee. Click an invoice number to see it as the accounting system has it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pattern_filter && (
                <div class="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-blue-200 bg-blue-50/70 px-3 py-2 text-sm text-blue-800">
                  <span>
                    Showing <span class="font-medium">{pattern_filter.customer_name}</span> invoices paid about{" "}
                    <span class="font-medium">{pattern_filter.percent.toFixed(2)}%</span> under invoice
                  </span>
                  <Button variant="outline" size="sm" class="h-6 gap-1 px-2 text-xs" onClick={() => select_pattern(null)}>
                    <X className="h-3 w-3" /> Clear filter
                  </Button>
                </div>
              )}
              {visible_detected.length === 0 ? (
                <div class="py-12 text-center text-sm text-slate-500">
                  {pattern_filter
                    ? "No detected invoices left in this pattern."
                    : "No unexplained gaps between synced invoices and what customers paid."}
                </div>
              ) : (
                <DetectedShortPaysTable
                  rows={visible_detected}
                  saving_key={savingId}
                  on_record={(row: DetectedShortPayJson, status: ShortPayStatus) => void record_with_note(row, status)}
                  on_dispute={(row: DetectedShortPayJson) => void dispute_detected(row)}
                  on_view_invoice={setViewingInvoice}
                />
              )}
            </CardContent>
          </Card>

          <Card class="shadow-sm border-slate-200/80">
            <CardHeader class="space-y-1 pb-4">
              <CardTitle class="text-lg font-semibold tracking-tight leading-snug">
                {open_rows.length} open {open_rows.length === 1 ? "short pay" : "short pays"} ·{" "}
                {exactCurrencyFormatter(sum_amounts(open_rows))}
              </CardTitle>
              <CardDescription>
                Tracked short pays — flagged when cash was applied, or recorded from the detected list. Resolve when repaid
                or disputed; ignore known contractual fees.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div class="flex gap-1.5 mb-4">
                {STATUS_TABS.map((t) => (
                  <Button
                    key={t.key}
                    variant={tab === t.key ? "default" : "outline"}
                    size="sm"
                    class="h-7 text-xs"
                    onClick={() => setTab(t.key)}>
                    {t.label}
                  </Button>
                ))}
              </div>

              {visible_recorded.length === 0 ? (
                <div class="py-12 text-center text-sm text-slate-500">No {tab === "all" ? "" : `${tab} `}short pays.</div>
              ) : (
                <RecordedShortPaysTable
                  rows={visible_recorded}
                  saving_id={savingId}
                  on_set_status={set_status}
                  on_file_dispute={setDisputing}
                  on_view_invoice={setViewingInvoice}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      <ShortPayDisputeDialog
        target={disputing ? { kind: "short_pay", short_pay: disputing } : null}
        onClose={() => setDisputing(null)}
        onFiled={() => void on_dispute_filed()}
      />
      <ShortPayInvoiceDialog
        invoice={viewing_invoice}
        accounting_system={data.accounting_system}
        pdf_available={data.invoice_pdf_available}
        onClose={() => setViewingInvoice(null)}
      />
    </div>
  )
}
